import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Shield, Zap, Target, Globe, Users, BarChart3,
  ArrowRight, ChevronRight, Package, Brain, Lock, Truck,
  Heart, Star, Award, Briefcase, Code, Database, Mail
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/wms/about';
const ICON_MAP = { Shield, Zap, Target, Globe, Users, BarChart3, Package, Brain, Lock, Truck, Heart, Star, Award, Briefcase, Code, Database };

/* ── Design Tokens ─────────────────────────────────────── */
const C = {
  primary:    '#007BFF',
  secondary:  '#2C2C2C',
  accent:     '#00FFFF',
  bg:         '#F9F9F9',
  dark:       '#0B0F19',
  card:       '#FFFFFF',
  shadow:     '0 2px 20px #0000001A',
  text:       '#1A1A1A',
  muted:      '#6B7280',
};
const FH = "'Orbitron', sans-serif";
const FB = "'Inter', 'Helvetica Neue', system-ui, sans-serif";

const IMG = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/ef4004e5-fbbe-4b40-8afe-7de797131be9/images/44bfbac661d054263fc56ee92bbe5d876e68291f95647a5b82c864974cd2ffaa.png',
  story: 'https://static.prod-images.emergentagent.com/jobs/ef4004e5-fbbe-4b40-8afe-7de797131be9/images/e88fcffbd4babd5e3d0a11b1730b285c53e6a0aa4f567abc481c4a5e1c3bee78.png',
  platform: 'https://static.prod-images.emergentagent.com/jobs/ef4004e5-fbbe-4b40-8afe-7de797131be9/images/b49ec35a147760c8a5cf9bc9afdd0b26df271c1e0651d69a97f293475edef5e9.png',
  hq: 'https://static.prod-images.emergentagent.com/jobs/ef4004e5-fbbe-4b40-8afe-7de797131be9/images/5e4c92a70725ec17e84d5b60c45645a6779e6a8633ef67b4ff164624ad4457e1.png',
};

const fade = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } })
};

/* ── Reusable ──────────────────────────────────────────── */
function Overline({ children, light }) {
  return (
    <span className="inline-block text-[11px] font-bold uppercase tracking-[0.3em]"
      style={{ fontFamily: FH, color: light ? C.accent : C.primary }}>{children}</span>
  );
}

function BtnPrimary({ children, to, ...props }) {
  const cls = "inline-flex items-center gap-2 px-7 py-3.5 rounded-md font-semibold text-[15px] text-white transition-all active:scale-95 hover:shadow-[0_0_28px_rgba(0,123,255,0.45)]";
  const style = { fontFamily: FH, background: C.primary };
  if (to) return <Link to={to} className={cls} style={style} {...props}>{children}</Link>;
  return <button className={cls} style={style} {...props}>{children}</button>;
}

function BtnSecondary({ children, ...props }) {
  return (
    <a className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md font-medium text-[15px] border-2 transition-all active:scale-95 hover:shadow-[0_0_20px_rgba(0,255,255,0.25)]"
      style={{ borderColor: C.accent, color: C.accent, fontFamily: FB }} {...props}>{children}</a>
  );
}

/* ── Page ───────────────────────────────────────────────── */
export default function AboutPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    document.body.style.background = C.secondary;
    document.documentElement.style.background = C.secondary;
    axios.get(`${API}/content`).then(r => setData(r.data)).catch(() => {});
    return () => { document.body.style.background = ''; document.documentElement.style.background = ''; };
  }, []);

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: C.dark }}>
      <div className="w-6 h-6 rounded-full animate-spin" style={{ border: `2px solid ${C.secondary}`, borderTopColor: C.primary }} />
    </div>
  );

  const { company_name, hero, stats, story, values, capabilities, team, cta, footer } = data;

  return (
    <div className="min-h-screen" style={{ fontFamily: FB, background: C.bg, color: C.text }} data-testid="about-page">

      {/* ── Sticky Nav — Charcoal Gray ────────────────────── */}
      <nav className="fixed top-0 w-full z-50 border-b border-white/10" style={{ background: C.secondary }}>
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/about-us" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: C.primary }}>
              <Package className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-[14px] text-white tracking-widest uppercase font-bold" style={{ fontFamily: FH }}>
              Agora
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/wms" className="text-sm text-gray-400 hover:text-white transition-colors font-medium" style={{ fontFamily: FB }} data-testid="nav-wms">Dashboard</Link>
            <Link to="/wms/login" className="text-sm font-semibold text-white px-5 py-2 rounded-md transition-all hover:opacity-90" style={{ background: C.primary, fontFamily: FH }} data-testid="nav-login">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════════
          HERO — Full-width dark section with warehouse image
          ═══════════════════════════════════════════════════════ */}
      <section className="relative min-h-[92vh] flex items-center px-6 overflow-hidden" data-testid="hero-section"
        style={{ backgroundImage: `url(${IMG.hero})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,15,25,0.88), rgba(11,15,25,0.55), rgba(11,15,25,0.25))' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(11,15,25,1) 0%, transparent 40%)' }} />

        <div className="max-w-[1200px] mx-auto w-full pt-20 relative z-10">
          <motion.div initial="hidden" animate="visible" className="max-w-2xl">
            {hero?.badge_text && (
              <motion.div variants={fade} custom={0}
                className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full mb-8 border"
                style={{ borderColor: `${C.accent}40`, color: C.accent, background: 'rgba(0,255,255,0.06)' }}>
                <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />{hero.badge_text}
              </motion.div>
            )}

            <motion.h1 variants={fade} custom={0.4}
              className="uppercase leading-[1.1]"
              style={{ fontFamily: FH, fontSize: 48, fontWeight: 700, letterSpacing: '0.04em', color: '#FFFFFF' }}>
              {hero?.heading_line1 || 'The future of'}<br />
              <span style={{ color: C.primary }}>{hero?.heading_line2 || 'warehouse intelligence'}</span>
            </motion.h1>

            <motion.p variants={fade} custom={0.8}
              className="mt-7 max-w-lg text-justify"
              style={{ fontSize: 17, lineHeight: 1.6, color: '#A0AEC0' }}>
              {hero?.description}
            </motion.p>

            <motion.div variants={fade} custom={1.2} className="mt-10 flex gap-4">
              <BtnPrimary to={hero?.cta_link || '/wms/login'} data-testid="hero-cta">
                {hero?.cta_text || 'Access WMS'} <ArrowRight className="w-4 h-4" />
              </BtnPrimary>
              <BtnSecondary href="#story" data-testid="hero-story-link">
                Our Story <ChevronRight className="w-4 h-4" />
              </BtnSecondary>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats Bar — Dark strip ─────────────────────────── */}
      {stats?.length > 0 && (
        <section style={{ background: C.dark }} data-testid="stats-section">
          <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <motion.div key={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade} custom={i * 0.08}
                className="py-12 sm:py-16 text-center">
                <div className="uppercase" style={{ fontFamily: FH, fontSize: 40, fontWeight: 700, color: C.accent, letterSpacing: '0.03em' }}>{s.value}</div>
                <div className="text-[10px] mt-2 uppercase tracking-[0.25em] font-semibold" style={{ color: '#6B7280' }}>{s.label}</div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════
          OUR STORY — Light section (Off-White background)
          ═══════════════════════════════════════════════════════ */}
      <section id="story" className="py-24 sm:py-32 px-6" style={{ background: C.bg }} data-testid="story-section">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade}>
              <Overline>Our Story</Overline>
              <h2 className="mt-4 leading-tight uppercase"
                style={{ fontFamily: FH, fontSize: 32, fontWeight: 500, color: C.secondary }}>
                {story?.heading}
              </h2>
            </motion.div>
            <div className="mt-8 space-y-5">
              {(story?.paragraphs || []).map((p, i) => (
                <motion.p key={i}
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade} custom={i * 0.12}
                  className="text-justify"
                  style={{ fontSize: 16, lineHeight: 1.6, color: i === (story.paragraphs.length - 1) ? C.text : C.muted, fontWeight: i === (story.paragraphs.length - 1) ? 500 : 400 }}>
                  {p}
                </motion.p>
              ))}
            </div>
          </div>
          <motion.div className="lg:col-span-7" initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="rounded-lg overflow-hidden" style={{ boxShadow: C.shadow }}>
              <img src={IMG.story} alt="Data network" className="w-full aspect-[16/10] object-cover" loading="lazy" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Core Values — White cards on light bg ──────────── */}
      {values?.length > 0 && (
        <section className="py-24 sm:py-32 px-6" style={{ background: '#F0F2F5' }} data-testid="values-section">
          <div className="max-w-[1200px] mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade} className="mb-14">
              <Overline>Core Values</Overline>
              <h2 className="mt-4 uppercase" style={{ fontFamily: FH, fontSize: 32, fontWeight: 500, color: C.secondary }}>What we stand for</h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((v, i) => {
                const IconComp = ICON_MAP[v.icon] || Target;
                const accents = [C.primary, C.accent, '#FF6F00', '#7C3AED', '#10B981', '#F59E0B'];
                const ac = accents[i % accents.length];
                return (
                  <motion.div key={i}
                    initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade} custom={i * 0.08}
                    className="rounded-lg p-6 transition-all duration-300 group hover:-translate-y-1"
                    style={{ background: C.card, boxShadow: C.shadow, borderLeft: `4px solid ${ac}` }}
                    data-testid={`value-${i}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: `${ac}15` }}>
                        <IconComp className="w-5 h-5" style={{ color: ac }} strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="mb-2 uppercase tracking-wide"
                          style={{ fontFamily: FH, fontSize: 13, fontWeight: 600, color: C.secondary }}>{v.title}</h3>
                        <p style={{ fontSize: 13, lineHeight: 1.65, color: C.muted }}>{v.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Platform Capabilities — Dark section ──────────── */}
      {capabilities?.length > 0 && (
        <section className="py-24 sm:py-32 px-6" style={{ background: C.dark }} data-testid="capabilities-section">
          <div className="max-w-[1200px] mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade} className="mb-14">
              <Overline light>Platform Capabilities</Overline>
              <h2 className="mt-4 uppercase" style={{ fontFamily: FH, fontSize: 32, fontWeight: 500, color: '#FFFFFF' }}>Enterprise-grade from day one</h2>
            </motion.div>
            <div className="grid lg:grid-cols-12 gap-6">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade}
                className="lg:col-span-5 lg:row-span-2 rounded-lg overflow-hidden relative group" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <img src={IMG.platform} alt="AI nodes" className="w-full h-full object-cover min-h-[320px] group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(11,15,25,0.95) 10%, rgba(11,15,25,0.3) 60%, transparent)' }} />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] mb-2" style={{ color: C.accent, fontFamily: FH }}>Intelligent Core</p>
                  <h3 className="text-lg font-bold text-white" style={{ fontFamily: FH }}>AI-Powered Orchestration</h3>
                  <p className="text-sm mt-2" style={{ color: '#9CA3AF', lineHeight: 1.6 }}>Real-time decision engine processing millions of inventory events across your supply chain.</p>
                </div>
              </motion.div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
                {capabilities.slice(0, 6).map((c, i) => {
                  const IconComp = ICON_MAP[c.icon] || Package;
                  return (
                    <motion.div key={i}
                      initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade} custom={i * 0.06}
                      className="rounded-lg p-6 sm:p-7 transition-all duration-300 group hover:border-white/20"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                      data-testid={`capability-${i}`}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 transition-colors"
                        style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <IconComp className="w-5 h-5 group-hover:text-white transition-colors" style={{ color: C.accent }} strokeWidth={1.5} />
                      </div>
                      <h3 className="mb-1.5 uppercase tracking-wide"
                        style={{ fontFamily: FH, fontSize: 12, fontWeight: 600, color: '#FFFFFF' }}>{c.title}</h3>
                      <p className="text-justify" style={{ fontSize: 13, lineHeight: 1.65, color: '#9CA3AF' }}>{c.desc}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Leadership — Light with HQ background ─────────── */}
      {team?.members?.length > 0 && (
        <section className="py-24 sm:py-32 px-6 relative overflow-hidden" style={{ background: C.bg }} data-testid="team-section">
          <div className="absolute inset-0 opacity-5">
            <img src={IMG.hq} alt="Singapore HQ" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="max-w-[1200px] mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-start mb-14">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade}>
                <Overline>Leadership</Overline>
                <h2 className="mt-4 leading-tight uppercase" style={{ fontFamily: FH, fontSize: 32, fontWeight: 500, color: C.secondary }}>{team?.heading}</h2>
                <p className="mt-5 text-justify" style={{ fontSize: 16, lineHeight: 1.6, color: C.muted }}>{team?.description}</p>
              </motion.div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {team.members.map((m, i) => (
                <motion.div key={i}
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade} custom={i * 0.08}
                  className="rounded-lg p-6 transition-all duration-300 hover:-translate-y-1"
                  style={{ background: C.card, boxShadow: C.shadow, border: '1px solid #E5E7EB' }}
                  data-testid={`leader-${i}`}>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                    style={{ background: `linear-gradient(135deg, ${C.primary}, #0056B3)` }}>
                    <span className="text-white font-bold text-lg" style={{ fontFamily: FH }}>
                      {m.name ? m.name.split(' ').map(n => n[0]).join('') : '?'}
                    </span>
                  </div>
                  <h3 className="font-bold" style={{ fontFamily: FH, fontSize: 13, color: C.secondary }}>{m.name}</h3>
                  <p className="text-xs font-semibold mt-1" style={{ color: C.primary }}>{m.role}</p>
                  <p className="mt-3 text-justify" style={{ fontSize: 12, lineHeight: 1.6, color: C.muted }}>{m.bio}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA — Dark gradient section ───────────────────── */}
      <section className="py-24 sm:py-32 px-6 relative overflow-hidden" style={{ background: C.dark }} data-testid="cta-section">
        <div className="absolute inset-0 opacity-20" style={{ background: `radial-gradient(ellipse at bottom, ${C.primary}30, transparent 70%)` }} />
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade}
          className="max-w-[800px] mx-auto text-center relative z-10">
          <h2 className="uppercase leading-[1.12]"
            style={{ fontFamily: FH, fontSize: 44, fontWeight: 700, color: '#FFFFFF' }}>{cta?.heading || 'Intelligence in Motion'}</h2>
          <p className="mt-6 mx-auto max-w-xl text-justify"
            style={{ fontSize: 17, lineHeight: 1.6, color: '#9CA3AF' }}>{cta?.description}</p>
          <div className="mt-12 flex justify-center gap-4">
            <BtnPrimary to={cta?.button_link || '/wms/login'} data-testid="cta-signin">
              {cta?.button_text || 'Get Started'} <ArrowRight className="w-5 h-5" />
            </BtnPrimary>
          </div>
        </motion.div>
      </section>

      {/* ── Footer ─────────────────────────────────────────── */}
      <footer className="py-10 px-6" style={{ background: C.secondary, borderTop: '1px solid rgba(255,255,255,0.08)' }} data-testid="footer">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: C.primary }}>
              <Package className="w-3 h-3 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-sm font-semibold text-white" style={{ fontFamily: FH, letterSpacing: '0.1em' }}>Agora Tech</span>
          </div>
          <p className="text-xs" style={{ color: '#6B7280' }}>
            {footer?.company_line || 'Agora Pte. Ltd.'} &middot; {new Date().getFullYear()}
          </p>
          <div className="flex items-center gap-6 text-xs" style={{ color: '#9CA3AF' }}>
            <a href="mailto:support@agora-sg.com" className="hover:text-white transition-colors flex items-center gap-1.5" data-testid="footer-support-email">
              <Mail className="w-3.5 h-3.5" />support@agora-sg.com
            </a>
            <Link to="/about-us" className="hover:text-white transition-colors">About</Link>
            <Link to="/wms" className="hover:text-white transition-colors">Dashboard</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
